import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import {
  Args,
  CLTypeUInt8,
  CLValue,
  ContractCallBuilder,
  PublicKey
} from 'casper-js-sdk';
import { useClickRef } from '@make-software/csprclick-ui';
import { TransactionStatus } from '@make-software/csprclick-core-types';
import { SvgIcon } from '@make-software/cspr-design';

import LoadingIcon from '@/assets/icons/loading.svg';
import { encrypt } from '@/utils/encryption';
import { ConnectedAccountContext } from '@/contexts';
import { BuyStampsFlow, ConfirmationModal, TextModal } from '@/components';
import { SendMessageForm } from './send-message-form';

const StyledLoadingIcon = styled(SvgIcon)(() => ({
  animationName: 'spin',
  animationDuration: '5000ms',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear'
}));

const buildSendMessageTransaction = async (
  senderPublicKey: string,
  recipientPublicKey: string,
  message: string,
) => {
  const body = await encrypt(recipientPublicKey.slice(2), message);
  const bodyBytes: CLValue[] = [];
  for (let i = 0; i < body.length; i += 1) {
    bodyBytes.push(CLValue.newCLUint8(body[i]));
  }

  const sendersCopy = await encrypt(senderPublicKey.slice(2), message);
  const sendersCopyBytes: CLValue[] = [];
  for (let i = 0; i < sendersCopy.length; i += 1) {
    sendersCopyBytes.push(CLValue.newCLUint8(sendersCopy[i]));
  }

  return new ContractCallBuilder()
    .chainName(window.csprclick?.chainName!)
    .from(PublicKey.fromHex(senderPublicKey))
    .payment(config.message_transaction_cost * 1000000000)
    .byPackageHash(config.chainmail_contract_package_hash)
    .entryPoint('send_message')
    .runtimeArgs(
      Args.fromMap({
        recipient: CLValue.newCLPublicKey(PublicKey.fromHex(recipientPublicKey)),
        body: CLValue.newCLList(CLTypeUInt8, bodyBytes),
        senders_copy: CLValue.newCLList(CLTypeUInt8, sendersCopyBytes),
        sender: CLValue.newCLPublicKey(PublicKey.fromHex(senderPublicKey)),
        parent_id: CLValue.newCLOption(CLValue.newCLUInt256(0)),
        thread_id: CLValue.newCLOption(CLValue.newCLUInt256(0)),
      }),
    )
    .build()
    .toJSON();
};

type SendMessageFlowStep = 'form'
  | 'loading'
  | 'success'
  | 'cancelled'
  | 'error'
  | 'buy_stamps_warning'
  | 'buy_stamps_flow'
  | 'buy_stamps_confirmation';

export interface SendMessageFlowProps {
  recipientPublicKey?: string;
  onSuccess: () => void;
}

export const SendMessageFlow: React.FC<SendMessageFlowProps> = ({ onSuccess, recipientPublicKey }) => {
  const { connectedAccount, stampBalance } = useContext(ConnectedAccountContext);
  const [flowStep, setFlowStep] = useState<SendMessageFlowStep>(!stampBalance ? 'buy_stamps_warning' : 'form');

  const clickRef = useClickRef();

  const submitSendMessageTransaction = async (recipient: string, message: string) => {
    const caller = connectedAccount?.public_key?.toLowerCase() || '';

    const transaction = await buildSendMessageTransaction(caller, recipient, message);

    const onStatusUpdate = (status: string, data: any) => {
      if (status === TransactionStatus.CANCELLED) {
        setFlowStep('cancelled');
      }
      else if (status === TransactionStatus.ERROR) {
        console.error('Error: ', data?.error + '(' + data?.errorData + ')');
        setFlowStep('error');
      }
      else if (status === TransactionStatus.PROCESSED) {
        if (data.csprCloudTransaction?.error_message === null) {
          setFlowStep('success');
          onSuccess();
        } else {
          console.error('Error: ', data?.error + '(' + data?.errorData + ')');
          setFlowStep('error');
        }
      }
    };

    setFlowStep('loading');

    clickRef?.send(transaction as object, caller, onStatusUpdate).catch((err: any) => {
      setFlowStep('error');
      alert('Error: ' + err);
    });
  };

  return (
    <>
      {flowStep === 'buy_stamps_warning' && (
        <ConfirmationModal
          text="You need to have stamps to send messages"
          onSubmit={() => setFlowStep('buy_stamps_flow')}
        />
      )}
      {flowStep === 'buy_stamps_flow' && (
        <BuyStampsFlow onSuccess={() => setFlowStep('buy_stamps_confirmation')} />
      )}
      {flowStep === 'buy_stamps_confirmation' && (
        <ConfirmationModal
          text="You have successfully purchased stamps!"
          onSubmit={() => setFlowStep('form')}
        />
      )}
      {flowStep === 'loading' && (
        <TextModal
          icon={<StyledLoadingIcon src={LoadingIcon} width={100} height={100}/>}
          text="Sending message"
        />
      )}
      {flowStep === 'success' && (
        <TextModal text="You have successfully sent your message!"/>
      )}
      {flowStep === 'cancelled' && (
        <TextModal text="Your request have been cancelled by the wallet application"/>
      )}
      {flowStep === 'error' && (
        <TextModal text="Something went wrong. Please try again later."/>
      )}
      {flowStep === 'form' && (
        <SendMessageForm
          recipientPublicKey={recipientPublicKey}
          onSubmit={submitSendMessageTransaction}
        />
      )}
    </>
  );
};
